import { Injectable } from '@angular/core';
import { marked, Tokens } from 'marked';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { RequestDto } from 'src/portal/ai-proxy-api/models/request-dto';
import { RoleEnum } from 'src/portal/ai-proxy-api/models/role-enum';
import { AuthService } from 'src/app/core/services/auth.service';
import { AddHistoryService } from 'src/portal/ai-proxy-api/services/add-history.service';
import { ChatEnrichmentConfigService } from '../configs/chat-enrichment.config';
import { AiChatProxyService } from './ai-chat-proxy.service';
import { GeminiAgentService } from './gemini-agent.service';
import { GeminiService } from './gemini-service.service';
import { GEMINI_LOADING_MESSAGES } from './gemini-loading-messages.constant';
import { INSTRUCTIONS } from './instructions.constants';
import { GeminiSchemaType, ProxyAiResponseSchemaService } from './proxy-ai-response-schema.service';
import { ApiDocsAgentService } from './api-docs-agent.service';
import { TrendAgentEvent, TrendAgentService } from './trend-agent.service';

export interface GeminiSourceItem {
  [key: string]: string | number | null | undefined;
}

export interface ChatMessage {
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isTyping?: boolean;
  generatedUi?: GeneratedUiSpec;
  generatedUiLoading?: boolean;
  trendAgentResponse?: boolean;
  companies?: CompanyDomain[];
  geminiSources?: GeminiSourceItem[];
  buyercaddySource?: unknown[];
  geminiSchemaType?: GeminiSchemaType;
  geminiColumns?: string[];
  geminiMode?: ChatCommand;
  loadingMessage?: string;
  loadingMessageVersion?: number;
}

export interface CompanyDomain {
  company_name: string;
  domain: string;
}

export type GeneratedUiBlock =
  | GeneratedUiMetricCardsBlock
  | GeneratedUiScoreTableBlock
  | GeneratedUiChartBlock
  | GeneratedUiTimelineBlock
  | GeneratedUiDiagramBlock
  | GeneratedUiRecommendationsBlock;

export interface GeneratedUiSpec {
  version: string;
  blocks: GeneratedUiBlock[];
}

export interface GeneratedUiMetricCardsBlock {
  id?: string;
  type: 'metric_cards';
  title: string;
  items: Array<{
    label: string;
    value: string;
    detail?: string;
    tone?: 'positive' | 'neutral' | 'negative' | 'warning';
  }>;
}

export interface GeneratedUiScoreTableBlock {
  id?: string;
  type: 'score_table';
  title: string;
  columns: string[];
  rows: string[][];
}

export interface GeneratedUiChartBlock {
  id?: string;
  type: 'bar_chart' | 'line_chart' | 'scatter_chart' | 'radar_chart' | 'historical_trend_chart';
  title: string;
  xLabel?: string;
  yLabel?: string;
  series: Array<{
    name: string;
    points: Array<{
      label: string;
      value: number;
    }>;
  }>;
  events?: Array<{
    date: string;
    label: string;
    detail?: string;
    sourceType?: string;
    confidence?: 'High' | 'Medium' | 'Low';
  }>;
}

export interface GeneratedUiTimelineBlock {
  id?: string;
  type: 'timeline';
  title: string;
  items: Array<{
    horizon: string;
    event: string;
    indicator?: string;
    risk?: string;
  }>;
}

export interface GeneratedUiDiagramBlock {
  id?: string;
  type: 'diagram';
  title: string;
  diagramType: string;
  layoutHint?: string;
  nodes: Array<{
    id: string;
    label: string;
    group?: string;
  }>;
  edges: Array<{
    from: string;
    to: string;
    label?: string;
  }>;
}

export interface GeneratedUiRecommendationsBlock {
  id?: string;
  type: 'recommendations';
  title: string;
  items: string[];
}

export enum ChatCommand {
  FindEmployees = 'Find profiles of employees',
  FindCustomers = 'Find customers of products',
  AnalyzeTechStacks = 'Analyze tech stacks for company',
  RecommendSoftware = 'Recommend software',
  BenchmarkCompanies = 'Benchmark companies',
  GeneralMode = 'General Mode',
  ProductAnalysis = 'Product Analysis',
  TrendAnalysis = 'Trend Analysis',
  ApiDocs = 'API Documentation',
}

interface CompanyDomainsData {
  company_domains?: unknown;
  [key: string]: unknown;
}

interface ModelConfig {
  systemInstruction: string;
  temperature?: number;
  tools?: object[];
}

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private chatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
  public chatHistory$: Observable<ChatMessage[]> =
    this.chatHistorySubject.asObservable();
  private chatHistory: ChatMessage[] = [];

  public hiFlag = false;
  public selectedMode: ChatCommand = ChatCommand.GeneralMode;
  public companyDomains: CompanyDomain[] = [];
  public isLoading = false;
  public lastGeminiSourceSelection: GeminiSourceItem | undefined;
  public aggressiveCustomersMode = false;
  public isAutoModeEnabled = true;

  private loadingMessageInterval: any;


  private geminiSourceActionSubject = new Subject<GeminiSourceItem>();
  public geminiSourceAction$ = this.geminiSourceActionSubject.asObservable();

  private sessionId: string = '';
  private geminiSessionId: string = '';
  private modelConfig: ModelConfig = {
    systemInstruction: '',
  };
  private readonly generatedUiStartMarker = '[[RACHEL_UI_SPEC]]';
  private readonly generatedUiEndMarker = '[[/RACHEL_UI_SPEC]]';
  private readonly maxProxyHistoryMessages = 12;
  private readonly maxProxyHistoryPartLength = 1800;

  private readonly geminiColumnOrder: Partial<Record<GeminiSchemaType, string[]>> = {
    [GeminiSchemaType.Companies]: [
      'Company Name',
      'Product Name',
      'Industry',
      'Country/Region',
      'How Product Is Used',
      'Scope',
      'Source',
      'Probability',
      'dateFirstVerified',
      'dateLastVerified',
    ],
    [GeminiSchemaType.Profiles]: [
      'Name',
      'Title',
      'Department',
      'Level',
      'Location',
      'Core Skills',
      'Source',
    ],
  };

  constructor(
    private history: AddHistoryService,
    private enrichmentConfigService: ChatEnrichmentConfigService,
    private aiChatProxyService: AiChatProxyService,
    private proxyAiResponseSchemaService: ProxyAiResponseSchemaService,
    private geminiAgentService: GeminiAgentService,
    private geminiService: GeminiService,
    private apiDocsAgentService: ApiDocsAgentService,
    private trendAgentService: TrendAgentService,
    private authService: AuthService
  ) {
    this.initializeModel({
      systemInstruction: INSTRUCTIONS.systemInstructionMain,
    });
    this.setupMarkedRenderer();
  }

  private setupMarkedRenderer(): void {
    const renderer = new marked.Renderer();

    renderer.link = function ({ href, title, tokens }: Tokens.Link): string {
      const text = marked.parser(tokens);

      // Проверка на внутреннюю или относительную ссылку
      const isInternal = href.startsWith('/') || !href.startsWith('http');

      if (isInternal) {
        // Экранируем кавычки, если нужно
        return `<a href="${href}" role="button" class="router-link">${text}</a>`;
      }

      // Внешние ссылки
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}"${titleAttr} target="_blank" role="button" rel="noopener noreferrer">${text}</a>`;
    };
    renderer.code = ({ text, lang }) => {
      if (lang === 'json') {
        try {
          const parsedJson = JSON.parse(text);
          const formattedJson = JSON.stringify(parsedJson, null, 2);
          return `
                  <details class="mt-2 mb-2">
                  <summary><b>Expand / Collapse JSON</b></summary>
                  <pre><code class="language-json">${formattedJson}</code></pre>
                  </details>`;
        } catch (e) {
          return `<pre><code class="language-json">${text}</code></pre>`;
        }
      }
      // For all other languages, return standard formatted code block
      const langClass = lang ? ` class="language-${lang}"` : '';
      return `<pre><code${langClass}>${text}</code></pre>`;
    };
    marked.setOptions({
      renderer,
      gfm: true,
      breaks: false,
      async: false,
    });
  }

  private async renderMarkdown(text: string): Promise<string> {
    try {
      return await marked.parse(text, {
        gfm: true,
        breaks: false,
        async: false,
      }) as string;
    } catch (error) {
      console.warn('Failed to render markdown:', error);
      return text;
    }
  }

  private updateChatHistory(): void {
    this.chatHistorySubject.next([...this.chatHistory]);
  }

  private addMessage(message: ChatMessage): void {
    this.chatHistory.push(message);
    this.updateChatHistory();
  }

  private initializeModel(config: ModelConfig): void {
    this.sessionId = this.generateGuid();
    this.geminiSessionId = this.generateGuid();
    this.modelConfig = config;
    this.geminiAgentService.clearHistory();
    this.clearLoadingMessageRotation();
  }

  private configureMode(message: string): string {
    const modeConfigs: Record<ChatCommand, ModelConfig> = {
      [ChatCommand.FindEmployees]: {
        systemInstruction: INSTRUCTIONS.systemIstructionEmployee,
        temperature: 0.7,
      },
      [ChatCommand.FindCustomers]: {
        systemInstruction: INSTRUCTIONS.systemInstructionCompaniesUseProduct,
        temperature: 0.7,
      },
      [ChatCommand.AnalyzeTechStacks]: {
        systemInstruction:
          INSTRUCTIONS.systemInstructionCompanySoftwareAnalysis,
        temperature: 0.7,
      },
      [ChatCommand.RecommendSoftware]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.BenchmarkCompanies]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.GeneralMode]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.ProductAnalysis]: {
        systemInstruction: INSTRUCTIONS.systemInstructionProduct,
        temperature: 0.7,
      },
      [ChatCommand.TrendAnalysis]: {
        systemInstruction: INSTRUCTIONS.systemInstructionTrendAnalysis,
        temperature: 0.7,
      },
      [ChatCommand.ApiDocs]: {
        systemInstruction: INSTRUCTIONS.systemInstructionApiDocs,
        temperature: 0.1,
      },
    };

    const command = message as ChatCommand;

    if (modeConfigs[command] && this.canUseMode(command)) {
      this.selectedMode = command;
      if (this.selectedMode !== ChatCommand.FindCustomers) {
        this.aggressiveCustomersMode = false;
      }
      this.initializeModel(modeConfigs[command]);
      return command;
    }

    return message;
  }

  public setAggressiveCustomersMode(enabled: boolean): void {
    this.aggressiveCustomersMode = enabled;
  }

  private getAvailableAutoModeCommands(): ChatCommand[] {
    return Object.values(ChatCommand).filter(command => this.canUseMode(command));
  }

  private canUseMode(command: ChatCommand): boolean {
    return command !== ChatCommand.FindEmployees || this.authService.checkPeopleViewerRole();
  }

  async sendMessage(message: string): Promise<void> {
    this.isLoading = true;
    const trimmedMessage = message.trim();
    const isModeCommand = Object.values(ChatCommand).includes(trimmedMessage as ChatCommand);
    
    // Automatic Intent Detection
    if (
      this.isAutoModeEnabled &&
      this.selectedMode !== ChatCommand.TrendAnalysis &&
      !isModeCommand &&
      !this.isGreeting(trimmedMessage)
    ) {
      const commands = this.getAvailableAutoModeCommands();
      
      // Get recent history to provide context, truncating long bot responses to save tokens
      const recentHistory = this.chatHistory.slice(-4).map(msg => ({
        role: msg.sender,
        text: msg.text.substring(0, 300) + (msg.text.length > 300 ? '...' : '')
      }));

      const detectedIntent = await this.geminiService.detectIntent(
        message, 
        commands,
        this.selectedMode,
        recentHistory
      );
      
      if (detectedIntent && this.canUseMode(detectedIntent as ChatCommand) && detectedIntent !== this.selectedMode) {
        console.log(`Switching mode to: ${detectedIntent}`);
        this.configureMode(detectedIntent);
      }
    }

    const processedMessage = this.configureMode(trimmedMessage);
    const botMessage = this.createBotMessage();
    const shouldEnrich = this.shouldEnrichWithData(message);
    const isTrendModeSelection = processedMessage === ChatCommand.TrendAnalysis;

    if (shouldEnrich) {
      this.startLoadingMessageRotation(botMessage);
    }

    if (!this.isGreeting(processedMessage)) {
      this.handleUserMessage(processedMessage, botMessage);

      if (shouldEnrich) {
        await this.enrichWithDataByMode(message, botMessage);
        this.setDisplayingDataMessage(botMessage);
      }
    } else {
      this.hiFlag = true;
      this.addMessage(botMessage);
    }

    if (isTrendModeSelection) {
      botMessage.text = await this.renderMarkdown(
        'Trend Analysis mode is ready. Send a company, product, vendor, or market and I will map signals, history, risks, competitors, and next moves.'
      );
      botMessage.isTyping = false;
      botMessage.generatedUiLoading = false;
      this.clearLoadingMessageRotation();
      this.updateChatHistory();
      this.isLoading = false;
      return;
    }

    await this.sendToChat(processedMessage, botMessage);
  }

  private createBotMessage(): ChatMessage {
    const isGeneratedUiMode = this.selectedMode === ChatCommand.TrendAnalysis;

    return {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      isTyping: true,
      geminiMode: this.selectedMode,
      generatedUiLoading: false,
      generatedUi: isGeneratedUiMode ? { version: '1.0', blocks: [] } : undefined,
    };
  }

  private isGreeting(message: string): boolean {
    return message.startsWith('Hi!');
  }

  private handleUserMessage(
    userMessage: string,
    botMessage: ChatMessage
  ): void {
    this.addMessage({
      text: userMessage,
      sender: 'user',
      timestamp: new Date(),
      geminiMode: this.selectedMode,
    });
    this.addMessage(botMessage);
  }

  private shouldEnrichWithData(message: string): boolean {
    return (
      (this.selectedMode === ChatCommand.AnalyzeTechStacks &&
        message !== 'Analyze tech stacks for company') ||
      (this.selectedMode === ChatCommand.FindCustomers &&
        message !== 'Find customers of products') ||
      (this.selectedMode === ChatCommand.FindEmployees &&
        message !== 'Find profiles of employees')
    );
  }

  private getRandomGeminiLoadingMessage(): string {
    if (!GEMINI_LOADING_MESSAGES.length) {
      return 'Gathering external evidence';
    }
    const index = Math.floor(Math.random() * GEMINI_LOADING_MESSAGES.length);
    return GEMINI_LOADING_MESSAGES[index];
  }

  private startLoadingMessageRotation(botMessage: ChatMessage): void {
    this.clearLoadingMessageRotation();
    botMessage.loadingMessage = this.getRandomGeminiLoadingMessage();
    botMessage.loadingMessageVersion = (botMessage.loadingMessageVersion ?? 0) + 1;
    this.loadingMessageInterval = setInterval(() => {
      botMessage.loadingMessage = this.getRandomGeminiLoadingMessage();
      botMessage.loadingMessageVersion = (botMessage.loadingMessageVersion ?? 0) + 1;
      this.updateChatHistory();
    }, 5000);
  }

  private setDisplayingDataMessage(botMessage: ChatMessage): void {
    this.clearLoadingMessageRotation();
    botMessage.loadingMessage = 'Displaying data';
    botMessage.loadingMessageVersion = (botMessage.loadingMessageVersion ?? 0) + 1;
  }

  private clearLoadingMessageRotation(): void {
    if (this.loadingMessageInterval) {
      clearInterval(this.loadingMessageInterval);
      this.loadingMessageInterval = undefined;
    }
  }

  private async enrichWithDataByMode(
    message: string,
    botMessage: ChatMessage
  ): Promise<void> {
    const config = this.enrichmentConfigService.config[this.selectedMode];
    if (!config) return;

    //const prompt = config.promptPrefix +' '+ message;
    const prompt = message;
    const {
      instruction: geminiInstruction,
      rangeStart,
      rangeEnd,
    } = this.prepareGeminiInstruction(
      config.geminiInstruction ?? config.systemInstruction
    );
    const isAggressiveMode =
      this.selectedMode === ChatCommand.FindCustomers &&
      this.aggressiveCustomersMode;
    const geminiInstructionWithPush =
      geminiInstruction +
      ' ## !!! Do your absolute best to provide the maximum number of results possible !!!';

    const [dataResponse, geminiData] = await Promise.all([
      config.fetchDataFn(prompt,25).catch(error => {
        console.error('BuyerCaddy fetch failed:', error);
        return { data: [] };
      }),
      config.geminiFn(
        this.selectedMode,
        geminiInstructionWithPush,
        prompt,
        this.geminiSessionId,
        { aggressiveMode: isAggressiveMode }
      ).catch(error => {
        console.error('OtherDataSources fetch failed:', error);
        return undefined;
      }),
    ]);

    const data = dataResponse?.data ?? [];
    console.log(geminiData);
    
    const geminiSources = Array.isArray(geminiData)
      ? (geminiData as GeminiSourceItem[])
      : this.extractGeminiSources(geminiData);

    botMessage.geminiSources = geminiSources;
    botMessage.buyercaddySource = data;
    
    // Сохраняем нативную таблицу в историю ДО отправки промпта к ИИ
    this.saveNativeTableToHistory(message, botMessage.buyercaddySource, botMessage.geminiSources);

    this.updateSystemInstruction(
      data,
      geminiSources,
      config.systemInstruction,
      message
    );
  }

  public notifyGeminiSourceSelection(source: GeminiSourceItem): void {
    this.lastGeminiSourceSelection = source;
    this.geminiSourceActionSubject.next(source);
  }

  private prepareGeminiInstruction(rawInstruction?: string): {
    instruction: string;
    rangeStart: Date;
    rangeEnd: Date;
  } {
    const rangeEnd = new Date();
    const rangeStart = new Date(rangeEnd.getTime());
    rangeStart.setMonth(rangeStart.getMonth() - 6);

    if (!rawInstruction) {
      return {
        instruction: '',
        rangeStart,
        rangeEnd,
      };
    }

    const startDate = this.formatInstructionDate(rangeStart);
    const endDate = this.formatInstructionDate(rangeEnd);

    const instruction = rawInstruction
      .replace(/{{DATE_RANGE_START}}/g, startDate)
      .replace(/{{DATE_RANGE_END}}/g, endDate);

    return {
      instruction,
      rangeStart,
      rangeEnd,
    };
  }

  private formatInstructionDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }


  private generateRandomIsoDate(rangeStart: Date, rangeEnd: Date): string {
    const start = rangeStart.getTime();
    const end = rangeEnd.getTime();
    const randomTime = start + Math.random() * Math.max(end - start, 1);
    return this.formatInstructionDate(new Date(randomTime));
  }




  private updateSystemInstruction(
    data: any[],
    geminiData: any,
    systemInstructionText: string,
    message: string
  ): void {
    const isFindProfiles = this.selectedMode === ChatCommand.FindEmployees;
    const hasBuyerData = Array.isArray(data) && data.length > 0;
    const hasGeminiData = Array.isArray(geminiData) && geminiData.length > 0;

    let additionalText: string;
    const strictUsageInstruction = 'Displays all the data that came in the instructions.';
     

    if (isFindProfiles) {
      if (hasBuyerData || hasGeminiData) {
        const sections: string[] = [
          ` ## User profile data received for: ${message}\n +`,
        ];

        if (hasBuyerData) {
          sections.push(
            ` ######## - BuyerCaddy profiles: ${JSON.stringify(data, null, 2)}\n +`
          );
        }

        if (hasGeminiData) {
          sections.push(
            ` ######## - OtherDataSources profiles:\n${JSON.stringify(geminiData, null, 2)}\n+`
          );
        }

        additionalText = sections.join('');

        if (hasBuyerData) {
          additionalText +=
            `## Show this datasources in separate tables, use columns from the data, also add a column with the source name.
          ## BuyerCaddy datasource data priority over OtherDataSources profiles.
          ## For column 'expertises' rename column to 'Core Skills', and in data, Try to group up to 3-5 skills.Group related skills into categories (e.g., 'Programming', 'Design', 'Analytics') combine related skills and shorten the text to make it concise and easy to read. Apply these changes to all data in the column.
          ## For column 'jobTitle' rename to 'Title'.
          ## For column 'jobFunction' rename to 'Department'.
          ## For column 'companyCity' rename to 'Location'.
          ## For column 'name' Link Creation: For any row in the BuyerCaddy data that contains an id field, create a hyperlink to the username in the format: /contact/{id}. Example: If the id is prof-shli-61d73e2f009806b48eaec676c3f37a8c, the link should be contact/prof-shli-61d73e2f009806b48eaec676c3f37a8c.
          ## For column 'name' Need to make a link from the name using id (name must be the link text), example contact/prof-shli-61d73e2f009806b48eaec676c3f37a8c, in the format: /contact/{id}
          ## For column 'socialLinks' need to samely sign the link, for example, LinkedIn (url use from data if it exists) - Remove commas between links
          ## Always show BuyerCaddy data source in a separate table

          ## Instruction for Handling BuyerCaddy Data
            Source Restriction: Always and only use data from "BuyerCaddy" for responses related to this data source.
            Link Creation: For any row in the BuyerCaddy data that contains an id field, create a hyperlink to the username in the format: /contact/{id}.
            Example: If the id is prof-shli-61d73e2f009806b48eaec676c3f37a8c, the link should be contact/prof-shli-61d73e2f009806b48eaec676c3f37a8c.
            Consistency: This rule must be applied every time BuyerCaddy data is processed, without exception.
            Explanation of Changes:
            Clarity: The instruction is broken into clear, actionable points using bullet points for better readability.
            Emphasis on "Always": Explicitly stating "Always and only" reinforces that no other data source should be used for BuyerCaddy-related queries.
            Specificity: The link creation process is described step-by-step, with an example to avoid ambiguity.
            Consistency: Adding "without exception" ensures the AI understands this is a mandatory rule.
          ## Use only the profiles and any referenced products that are explicitly present in the provided datasets. Never invent new entities beyond these sources.`
        } else {
          additionalText +=
            ' ## BuyerCaddy returned no profiles for this query.\n' +
            ' ## Use the OtherDataSources profiles, but clearly label OtherDataSources as the source and never attribute them to BuyerCaddy.\n' +
            ' ## Present OtherDataSources data in its own table with an explicit source column.\n' +
            ' ## Use only the profiles and products included in the OtherDataSources dataset; never create additional entries.';
        }

        additionalText += strictUsageInstruction;
        additionalText +=
          ' ## Show every profile record that is present in the provided data (do not skip or hide entries).';
      } else {
        additionalText =
          ' ## No BuyerCaddy or OtherDataSources profile data found!\n' +
          'Try refining your prompt, e.g. include company name or domain.\n' +
          'Do not fabricate BuyerCaddy data or invent new profiles/products beyond the provided sources.\n' +
          'Only report that no relevant data was supplied.';
      }
    } else {
      if (hasBuyerData || hasGeminiData) {
        const sections: string[] = [
          ` This is an additional data source from BuyerCaddy for the company: ${message}\n +`,
        ];

        if (hasBuyerData) {
          sections.push(
            ` BuyerCaddy: ${JSON.stringify(data, null, 2)}\n +`
          );
        }

        if (hasGeminiData) {
          sections.push(
            ` OtherDataSources: ${JSON.stringify(geminiData, null, 2)}\n +`,
            ' For this additional source, the source is specified within the dataset itself (source field).\n +'
          );
        }

        if (!hasBuyerData) {
          sections.push(' ## BuyerCaddy data not available for this prompt.\n');
        }

        additionalText =
          sections.join('') +
          ' ## This is a dynamic data source, for each user prompt there is always data. ...' +
          ' ## Limit all products, companies, and technologies referenced in the answer to those explicitly listed in the provided datasets (BuyerCaddy or OtherDataSources). Do not invent or infer new products beyond them. When a requested product is missing, clearly report that the supplied sources do not contain it.';

        additionalText += strictUsageInstruction;
        additionalText +=
          ' ## CRITICAL: DO NOT format or output the raw data as markdown tables or lists yourself. Focus only on analytical text, summary, insights and trends. The UI will natively render the full data tables.';
      } else {
        additionalText =
          ' ## No BuyerCaddy or OtherDataSources data found for this prompt.\n' +
          'Inform the user that no external sources are available and do not reference BuyerCaddy.\n' +
          'Do not speculate or invent products, companies, or technologies that are absent from the provided data.\n' +
          'Respond by explaining that the supplied datasets did not include any entries relevant to the request.';
      }
    }

    this.modelConfig.systemInstruction = systemInstructionText + additionalText;
  }

  private extractGeminiSources(rawGeminiData: any): GeminiSourceItem[] {
    if (!rawGeminiData) {
      return [];
    }

    const collected: GeminiSourceItem[] = [];
    const visited = new Set<any>();
    const candidateKeys = ['source', 'sources','companies','products'];

    const enqueueSources = (value: unknown): void => {
      if (!Array.isArray(value)) {
        return;
      }
      const validItems = value.filter(
        item => item && typeof item === 'object'
      ) as GeminiSourceItem[];
      if (validItems.length) {
        collected.push(...validItems);
      }
    };

    const traverse = (node: unknown): void => {
      if (node === null || node === undefined) {
        return;
      }

      if (typeof node === 'string') {
        try {
          traverse(JSON.parse(node));
        } catch {
          /* ignore */
        }
        return;
      }

      if (typeof node !== 'object') {
        return;
      }

      if (visited.has(node)) {
        return;
      }
      visited.add(node);

      if (Array.isArray(node)) {
        for (const item of node) {
          traverse(item);
        }
        return;
      }

      const record = node as Record<string, unknown>;
      candidateKeys.forEach(key => enqueueSources(record[key]));

      const dataValue = record['data'];
      if (dataValue && typeof dataValue === 'object') {
        const dataRecord = dataValue as Record<string, unknown>;
        candidateKeys.forEach(key => enqueueSources(dataRecord[key]));
      }

      Object.values(record).forEach(value => traverse(value));
    };

    traverse(rawGeminiData);
    return collected;
  }

  private computeGeminiColumns(
    sources: GeminiSourceItem[] | undefined,
    schemaType?: GeminiSchemaType
  ): string[] {
    if (!sources?.length) {
      return [];
    }

    const preferredOrder = schemaType
      ? this.geminiColumnOrder[schemaType] ?? []
      : [];
    const encountered = new Set<string>();
    const orderedColumns: string[] = [];

    const addColumn = (column: string) => {
      if (column && !encountered.has(column)) {
        encountered.add(column);
        orderedColumns.push(column);
      }
    };

    for (const column of preferredOrder) {
      addColumn(column);
    }

    for (const source of sources) {
      Object.keys(source ?? {}).forEach(key => {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== '') {
          addColumn(key);
        }
      });
    }

    return orderedColumns;
  }

  private mapModeToGeminiSchemaType(
    mode: ChatCommand
  ): GeminiSchemaType | undefined {
    switch (mode) {
      case ChatCommand.FindEmployees:
        return GeminiSchemaType.Profiles;
      case ChatCommand.FindCustomers:
      case ChatCommand.AnalyzeTechStacks:
        return GeminiSchemaType.Companies;
      default:
        return undefined;
    }
  }


  private async sendToChat(
    processedMessage: string,
    botMessage: ChatMessage
  ): Promise<void> {
    try {
      if (this.selectedMode === ChatCommand.ApiDocs) {
        const responseText = await this.apiDocsAgentService.answerWithTools(
          this.modelConfig.systemInstruction,
          processedMessage
        );
        botMessage.text = await this.renderMarkdown(responseText);
        botMessage.isTyping = false;
        this.clearLoadingMessageRotation();
        this.updateChatHistory();
        this.isLoading = false;
        return;
      }

      if (this.selectedMode === ChatCommand.TrendAnalysis) {
        await this.sendToTrendAgent(processedMessage, botMessage);
        return;
      }
      
      const requestDto: RequestDto = {
        config: {
          systemInstruction: this.modelConfig.systemInstruction,
          temperature: this.modelConfig.temperature,
        },
        model: 'gemini-flash-latest',
        contents: this.buildProxyContents(processedMessage),
      };
      const resultStream = await this.aiChatProxyService.sendMessage(
        requestDto,
        this.sessionId
      );

      // this.chatSession.params.history = [];
      //const result = await this.chatSession.sendMessageStream(processedMessage);
      await this.processStreamResponse(
        processedMessage,
        resultStream,
        botMessage
      );
    } catch (error) {
      this.handleError(botMessage, error);
    } finally {
      this.isLoading = false;
    }
  }

  private async sendToTrendAgent(
    processedMessage: string,
    botMessage: ChatMessage
  ): Promise<void> {
    let accumulatedText = '';

    botMessage.trendAgentResponse = true;
    botMessage.generatedUi = botMessage.generatedUi ?? { version: '1.0', blocks: [] };
    botMessage.generatedUiLoading = true;
    botMessage.loadingMessage = 'Preparing trend analysis';
    botMessage.loadingMessageVersion = (botMessage.loadingMessageVersion ?? 0) + 1;
    this.updateChatHistory();

    for await (const event of this.trendAgentService.streamTrendAnalysis({
      conversationId: this.sessionId,
      message: processedMessage,
      mode: 'trend_analysis',
      context: {
        selectedMode: this.selectedMode,
        client: 'rachel-ui',
      },
    })) {
      if (event.type === 'text_delta') {
        accumulatedText += event.text;
        botMessage.text = await this.renderMarkdown(this.cleanGeneratedTextForDisplay(accumulatedText));
        this.updateChatHistory();
        continue;
      }

      await this.applyTrendAgentEvent(event, botMessage, accumulatedText);
    }

    botMessage.text = await this.renderMarkdown(this.cleanGeneratedTextForDisplay(accumulatedText));
    botMessage.generatedUiLoading = false;
    botMessage.isTyping = false;
    this.clearLoadingMessageRotation();
    this.updateChatHistory();
    this.saveHistory(processedMessage, this.buildTrendAgentHistoryResponse(accumulatedText, botMessage));
  }

  private buildTrendAgentHistoryResponse(markdownText: string, botMessage: ChatMessage): string {
    const visibleText = this.cleanGeneratedTextForDisplay(markdownText);
    const technicalData = {
      type: 'trend_analysis_response',
      mode: ChatCommand.TrendAnalysis,
      savedAt: new Date().toISOString(),
      generatedUi: botMessage.generatedUi ?? null,
    };

    return [
      visibleText,
      '',
      '---',
      '',
      '[[RACHEL_TECHNICAL_DATA]]',
      '```json',
      JSON.stringify(technicalData, null, 2),
      '```',
      '[[/RACHEL_TECHNICAL_DATA]]',
    ].join('\n');
  }

  private async applyTrendAgentEvent(
    event: TrendAgentEvent,
    botMessage: ChatMessage,
    accumulatedText: string
  ): Promise<void> {
    switch (event.type) {
      case 'state':
        botMessage.loadingMessage = event.label;
        botMessage.loadingMessageVersion = (botMessage.loadingMessageVersion ?? 0) + 1;
        this.updateChatHistory();
        break;
      case 'ui_block_create':
        this.upsertGeneratedUiBlock(botMessage, event.block);
        this.updateChatHistory();
        break;
      case 'ui_block_patch':
        this.upsertGeneratedUiBlock(botMessage, event.patch);
        this.updateChatHistory();
        break;
      case 'done':
        botMessage.generatedUiLoading = false;
        botMessage.isTyping = false;
        botMessage.loadingMessage = undefined;
        botMessage.text = await this.renderMarkdown(this.cleanGeneratedTextForDisplay(accumulatedText));
        this.updateChatHistory();
        break;
      case 'error':
        throw new Error(event.message);
    }
  }

  private upsertGeneratedUiBlock(botMessage: ChatMessage, rawBlock: unknown): void {
    const block = this.sanitizeGeneratedUiBlock(rawBlock);
    if (!block) {
      return;
    }

    botMessage.generatedUi = botMessage.generatedUi ?? { version: '1.0', blocks: [] };
    const blocks = botMessage.generatedUi.blocks;
    const blockKey = block.id || block.title;
    const existingIndex = blocks.findIndex(item => (item.id || item.title) === blockKey);

    if (existingIndex >= 0) {
      blocks[existingIndex] = block;
      return;
    }

    if (blocks.length < 10) {
      blocks.push(block);
    }
  }

  private buildProxyContents(currentMessage: string): RequestDto['contents'] {
    const completedModeMessages = this.chatHistory
      .filter(message =>
        !message.isTyping &&
        message.geminiMode === this.selectedMode &&
        (message.sender === 'user' || message.sender === 'bot')
      );

    const historyWithoutCurrent = [...completedModeMessages];
    const lastMessage = historyWithoutCurrent[historyWithoutCurrent.length - 1];
    if (lastMessage?.sender === 'user' && lastMessage.text.trim() === currentMessage.trim()) {
      historyWithoutCurrent.pop();
    }

    const historyContents = historyWithoutCurrent
      .slice(-this.maxProxyHistoryMessages)
      .map(message => {
        const text = this.normalizeHistoryTextForProxy(message.text);
        if (!text) {
          return undefined;
        }

        return {
          role: message.sender === 'user' ? RoleEnum.USER : RoleEnum.MODEL,
          parts: [text],
        };
      })
      .filter((content): content is RequestDto['contents'][number] => !!content);

    return [
      ...historyContents,
      {
        role: RoleEnum.USER,
        parts: [currentMessage],
      },
    ];
  }

  private normalizeHistoryTextForProxy(text: string): string {
    const withoutUiSpec = this.stripGeneratedUiSpec(text)
      .replace(/\[\[COMPANY_CARD\]\]/g, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\s+/g, ' ')
      .trim();

    return withoutUiSpec.slice(0, this.maxProxyHistoryPartLength);
  }

  private async processStreamResponse(
    processedMessage: string,
    stream: AsyncIterable<any>,
    botMessage: ChatMessage
  ): Promise<void> {
    let accumulatedText = '';

    // Отображаем текст в реальном времени из стрима
    for await (const chunk of stream) {
      accumulatedText += chunk
      botMessage.text = await this.renderMarkdown(this.cleanGeneratedTextForDisplay(accumulatedText));
      botMessage.text = botMessage.text.replace('```markdown', '').replace('```', '').replace('```\n', '').replace('markdown','').replace('`','').replace('json','');
      this.updateChatHistory();
    }
    //сохранение истории на сервер
    //this.saveHistory(processedMessage, accumulatedText);
    
    // Формируем карточки компаний напрямую из сырых данных агентов
    const companies = this.extractCompaniesFromSources(botMessage.buyercaddySource, botMessage.geminiSources, this.selectedMode);
    if (companies.length > 0) {
      accumulatedText += '\n\n[[COMPANY_CARD]]';
      botMessage.companies = companies;
    }

    botMessage.generatedUi = this.parseGeneratedUiSpec(accumulatedText) ?? botMessage.generatedUi;
    botMessage.generatedUiLoading = false;
    botMessage.text = await this.renderMarkdown(this.cleanGeneratedTextForDisplay(accumulatedText));
    botMessage.isTyping = false;

    //сохранение истории локально
    this.clearLoadingMessageRotation();
    this.updateChatHistory();
  }

  private cleanGeneratedTextForDisplay(text: string): string {
    return this.stripAsciiDiagramBlocks(
      this.unwrapAccidentalMarkdownCodeBlocks(
        this.stripGeneratedUiSpec(text)
      )
    );
  }

  private unwrapAccidentalMarkdownCodeBlocks(text: string): string {
    return text
      .replace(/```(?:markdown|md|text|txt|json)?\s*\n([\s\S]*?)(```|$)/gi, (match, body, closingFence) => {
        const cleanedBody = this.stripAccidentalJsonWrapper(body);
        if (this.isAnalyticalMarkdownBody(body)) {
          return cleanedBody;
        }

        if (!closingFence && this.isLikelyAccidentalUnclosedFence(body)) {
          return cleanedBody;
        }

        return this.isAsciiDiagramContent(cleanedBody) ? '' : match;
      })
      .replace(/^```(?:markdown|md|text|txt|json)?\s*$/gim, '')
      .replace(/(^|\n)\s*\{\s*\n(?=(?:\s*\n)*(?:#{1,3}\s+|\*+\s+|\d+\.\s+|\*\*|[A-ZА-Я]))/g, '$1')
      .replace(/\n\s*\}\s*$/g, '')
      .trim();
  }

  private isLikelyAccidentalUnclosedFence(body: string): boolean {
    const cleaned = this.stripAccidentalJsonWrapper(body);
    const hasDiagramSignals = /[|=<>^v]{4,}|-{3,}|_{3,}/.test(cleaned);
    const hasProseAfterwards = /\n\s*(#{1,3}\s+|[-*]\s+\*\*|\d+\.\s+|[A-ZА-Я][A-Za-zА-Яа-я ,.'"-]{35,})/.test(cleaned);
    return hasDiagramSignals || hasProseAfterwards;
  }

  private isAnalyticalMarkdownBody(body: string): boolean {
    const cleaned = this.stripAccidentalJsonWrapper(body);
    const wordCount = (cleaned.match(/[A-Za-zА-Яа-я0-9]{3,}/g) ?? []).length;
    const markdownSignals = [
      /^#{1,3}\s+/m,
      /^\s*[-*]\s+\*\*/m,
      /^\s*\d+\.\s+/m,
      /\*\*[^*]+\*\*/,
      /\n\n[A-ZА-Я][\s\S]{30,}/,
    ].filter(pattern => pattern.test(cleaned)).length;

    if (markdownSignals < 2 || wordCount < 25) {
      return false;
    }

    try {
      JSON.parse(body);
      return false;
    } catch {
      return true;
    }
  }

  private stripAccidentalJsonWrapper(body: string): string {
    return body
      .replace(/^\s*\{\s*/, '')
      .replace(/\s*\}\s*$/, '')
      .trim();
  }

  private stripGeneratedUiSpec(text: string): string {
    const start = text.indexOf(this.generatedUiStartMarker);
    if (start === -1) {
      return text;
    }

    const end = text.indexOf(this.generatedUiEndMarker, start + this.generatedUiStartMarker.length);
    if (end === -1) {
      return text.slice(0, start).trimEnd();
    }

    return (
      text.slice(0, start) +
      text.slice(end + this.generatedUiEndMarker.length)
    ).trim();
  }

  private stripAsciiDiagramBlocks(text: string): string {
    const withoutFencedDiagrams = this.stripUnclosedFencedDiagram(
      text.replace(/```(?:text|txt|diagram|ascii|markdown|md|json)?\s*\n([\s\S]*?)```/gi, (match, body) => {
        return this.isAsciiDiagramContent(body) ? '' : match;
      })
    );

    const lines = withoutFencedDiagrams.split('\n');
    const output: string[] = [];
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];
      const isIndentedOrDiagramLine =
        /^\s{2,}\S/.test(line) ||
        /^\s*[\[\(].+[\]\)]\s*$/.test(line) ||
        /^\s*[\/\\|^v<>-]+\s*$/.test(line);

      if (!isIndentedOrDiagramLine) {
        output.push(line);
        index += 1;
        continue;
      }

      const block: string[] = [];
      let cursor = index;
      while (cursor < lines.length) {
        const current = lines[cursor];
        if (!current.trim()) {
          break;
        }

        const continuesDiagram =
          /^\s{2,}\S/.test(current) ||
          /^\s*[\[\(].+[\]\)]\s*$/.test(current) ||
          /^\s*[\/\\|^v<>-]+\s*$/.test(current) ||
          /\s{3,}/.test(current);

        if (!continuesDiagram) {
          break;
        }

        block.push(current);
        cursor += 1;
      }

      if (this.isAsciiDiagramContent(block.join('\n'))) {
        index = cursor;
        continue;
      }

      output.push(...block);
      index = cursor;
    }

    return output.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  private stripUnclosedFencedDiagram(text: string): string {
    return text.replace(/```(?:text|txt|diagram|ascii|markdown|md|json)?\s*\n([\s\S]*)$/i, (match, body) => {
      const stripped = this.stripLeadingAsciiDiagram(body);
      if (stripped !== body) {
        return stripped;
      }

      return this.isAsciiDiagramContent(body) ? '' : match;
    });
  }

  private stripLeadingAsciiDiagram(body: string): string {
    const lines = body.split('\n');
    let cursor = 0;
    let sawDiagram = false;

    while (cursor < lines.length) {
      const line = lines[cursor] || '';
      const trimmed = line.trim();
      const isDiagramish =
        !trimmed ||
        /[|=<>^v]{3,}/.test(line) ||
        /^\s{2,}\S/.test(line) ||
        /trajectory|market share|estimated|axis|score|index/i.test(trimmed) ||
        /^[A-Z0-9 %().:&/-]{10,}$/.test(trimmed);

      if (!isDiagramish) {
        break;
      }

      if (trimmed) {
        sawDiagram = true;
      }
      cursor += 1;
    }

    if (!sawDiagram) {
      return body;
    }

    while (cursor < lines.length && !this.isProseBoundary(lines[cursor] || '')) {
      cursor += 1;
    }

    return lines.slice(cursor).join('\n').trimStart();
  }

  private isProseBoundary(line: string): boolean {
    const trimmed = line.trim();
    return (
      /^#{1,4}\s+/.test(trimmed) ||
      /^[-*]\s+\*\*/.test(trimmed) ||
      /^\d+\.\s+/.test(trimmed) ||
      /^[A-ZА-Я][A-Za-zА-Яа-я ,.'"():;-]{35,}/.test(trimmed)
    );
  }

  private isAsciiDiagramContent(content: string): boolean {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 3) {
      return false;
    }

    const labelBoxLines = lines.filter(line => /\[[^\]]+\]|\([^)]+\)/.test(line)).length;
    const connectorLines = lines.filter(line => /[\/\\|^v<>]|--|->|<-/.test(line)).length;
    const spacedLines = lines.filter(line => /\s{3,}/.test(line)).length;
    const connectorChars = (content.match(/[\/\\|^v<>]/g) ?? []).length;

    return (
      (labelBoxLines >= 2 && (connectorLines >= 2 || spacedLines >= 2)) ||
      (connectorLines >= 3 && spacedLines >= 2) ||
      connectorChars >= 6
    );
  }

  private parseGeneratedUiSpec(text: string): GeneratedUiSpec | undefined {
    const start = text.indexOf(this.generatedUiStartMarker);
    const end = text.indexOf(this.generatedUiEndMarker, start + this.generatedUiStartMarker.length);

    if (start === -1 || end === -1 || end <= start) {
      return undefined;
    }

    const rawJson = text
      .slice(start + this.generatedUiStartMarker.length, end)
      .trim()
      .replace(/^```json\s*/i, '')
      .replace(/```$/i, '')
      .trim();

    try {
      return this.sanitizeGeneratedUiSpec(JSON.parse(rawJson));
    } catch (error) {
      console.warn('Failed to parse generated UI spec:', error);
      return undefined;
    }
  }

  private sanitizeGeneratedUiSpec(rawSpec: unknown): GeneratedUiSpec | undefined {
    const spec = rawSpec as { version?: unknown; blocks?: unknown };
    if (!spec || !Array.isArray(spec.blocks)) {
      return undefined;
    }

    const blocks = spec.blocks
      .slice(0, 8)
      .map(block => this.sanitizeGeneratedUiBlock(block))
      .filter((block): block is GeneratedUiBlock => !!block);

    return {
      version: this.asText(spec.version, '1.0'),
      blocks,
    };
  }

  private sanitizeGeneratedUiBlock(rawBlock: unknown): GeneratedUiBlock | undefined {
    const block = rawBlock as Record<string, unknown>;
    const type = this.normalizeGeneratedUiType(block?.type);
    const title = this.asText(block?.title, 'Generated insight');

    switch (type) {
      case 'metric_cards':
        return {
          id: this.asText(block.id),
          type,
          title,
          items: this.asList(block.items || block.metrics)
            .slice(0, 6)
            .map(item => {
              const record = item as Record<string, unknown>;
              return {
                label: this.asText(record?.label || record?.name),
                value: this.asText(record?.value || record?.score),
                detail: this.asText(record?.detail || record?.description),
                tone: this.asTone(record?.tone),
              };
            })
            .filter(item => item.label && item.value),
        };
      case 'score_table': {
        const columns = this.asArray(block.columns)
          .slice(0, 8)
          .map(column => this.normalizeTableColumn(column))
          .filter(column => column.label);
        const rows = this.normalizeTableRows(block.rows, columns).slice(0, 12);

        return columns.length && rows.length
          ? { id: this.asText(block.id), type, title, columns: columns.map(column => column.label), rows }
          : undefined;
      }
      case 'bar_chart':
      case 'line_chart':
      case 'scatter_chart':
      case 'radar_chart':
      case 'historical_trend_chart':
        return {
          id: this.asText(block.id),
          type,
          title,
          xLabel: this.asText(block.xLabel),
          yLabel: this.asText(block.yLabel),
          series: this.asArray(block.series)
            .slice(0, 6)
            .map(series => {
              const record = series as Record<string, unknown>;
              return {
                name: this.asText(record?.name || record?.label, 'Series'),
                points: this.asArray(record?.points || record?.data)
                  .slice(0, 20)
                  .map(point => {
                    const pointRecord = this.asRecord(point);
                    if (!pointRecord && Array.isArray(point)) {
                      return {
                        label: this.asText(point[0]),
                        value: this.asNumber(point[1]),
                      };
                    }

                    return {
                      label: this.asText(pointRecord?.label || pointRecord?.name || pointRecord?.date || pointRecord?.period),
                      value: this.asNumber(pointRecord?.value || pointRecord?.score || pointRecord?.y),
                    };
                  })
                  .filter(point => point.label && Number.isFinite(point.value)),
              };
            })
            .filter(series => series.points.length),
          events: this.asArray(block.events)
            .slice(0, 20)
            .map(event => {
              const record = event as Record<string, unknown>;
              return {
                date: this.asText(record?.date || record?.period),
                label: this.asText(record?.label || record?.title),
                detail: this.asText(record?.detail || record?.description),
                sourceType: this.asText(record?.sourceType || record?.source_type),
                confidence: this.asConfidence(record?.confidence),
              };
            })
            .filter(event => event.date && event.label),
        };
      case 'timeline':
        return {
          id: this.asText(block.id),
          type,
          title,
          items: this.asArray(block.items || block.events)
            .slice(0, 8)
            .map(item => {
              const record = item as Record<string, unknown>;
              return {
                horizon: this.asText(record?.horizon || record?.date || record?.period),
                event: this.asText(record?.event || record?.label || record?.title),
                indicator: this.asText(record?.indicator),
                risk: this.asText(record?.risk),
              };
            })
            .filter(item => item.horizon && item.event),
        };
      case 'diagram':
        return {
          id: this.asText(block.id),
          type,
          title,
          diagramType: this.asText(block.diagramType, 'market_map'),
          layoutHint: this.asText(block.layoutHint),
          nodes: this.asArray(block.nodes)
            .slice(0, 20)
            .map(node => {
              const record = node as Record<string, unknown>;
              return {
                id: this.asText(record?.id || record?.name || record?.label),
                label: this.asText(record?.label || record?.name || record?.id),
                group: this.asText(record?.group),
              };
            })
            .filter(node => node.id && node.label),
          edges: this.asArray(block.edges || block.links)
            .slice(0, 30)
            .map(edge => {
              const record = edge as Record<string, unknown>;
              return {
                from: this.asText(record?.from || record?.source),
                to: this.asText(record?.to || record?.target),
                label: this.asText(record?.label || record?.relationship),
              };
            })
            .filter(edge => edge.from && edge.to),
        };
      case 'recommendations':
        return {
          id: this.asText(block.id),
          type,
          title,
          items: this.asList(block.items || block.recommendations)
            .slice(0, 8)
            .map(item => {
              const record = this.asRecord(item);
              return record
                ? this.asText(record.item || record.action || record.recommendation || record.text || record.label)
                : this.asText(item);
            })
            .filter(Boolean),
        };
      default:
        return undefined;
    }
  }

  private normalizeGeneratedUiType(value: unknown): string {
    const raw = this.asText(value).toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    const aliases: Record<string, string> = {
      metrics: 'metric_cards',
      metric_card: 'metric_cards',
      table: 'score_table',
      trend_chart: 'historical_trend_chart',
      historical_chart: 'historical_trend_chart',
      relationship_map: 'diagram',
      graph: 'diagram',
      network: 'diagram',
    };

    return aliases[raw] || raw;
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private asList(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    const record = this.asRecord(value);
    return record ? Object.values(record) : [];
  }

  private asRecord(value: unknown): Record<string, unknown> | undefined {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : undefined;
  }

  private normalizeTableColumn(column: unknown): { key: string; label: string } {
    const record = this.asRecord(column);
    if (record) {
      const label = this.asText(
        record.label ||
        record.title ||
        record.header ||
        record.name ||
        record.key ||
        record.field ||
        record.id
      );
      const key = this.asText(record.key || record.field || record.id || record.name || label);
      return { key, label };
    }

    const label = this.asText(column);
    return { key: label, label };
  }

  private normalizeTableRows(rows: unknown, columns: Array<{ key: string; label: string }>): string[][] {
    return this.asArray(rows)
      .map(row => {
        if (Array.isArray(row)) {
          return row.slice(0, columns.length || 8).map(cell => this.asText(cell));
        }

        const record = this.asRecord(row);
        if (record) {
          const lookup = new Map(
            Object.entries(record).map(([key, value]) => [this.normalizeTableKey(key), value])
          );
          return columns.map(column => this.asText(
            lookup.get(this.normalizeTableKey(column.key)) ??
            lookup.get(this.normalizeTableKey(column.label))
          ));
        }

        return [this.asText(row)];
      })
      .filter(row => row.some(cell => cell));
  }

  private normalizeTableKey(key: string): string {
    return key.toLowerCase().replace(/[^a-z0-9а-яё]+/gi, '');
  }

  private asText(value: unknown, fallback: string = ''): string {
    if (value === null || value === undefined) {
      return fallback;
    }

    return String(value).replace(/\s+/g, ' ').trim().slice(0, 240);
  }

  private asTone(value: unknown): 'positive' | 'neutral' | 'negative' | 'warning' {
    const tone = this.asText(value);
    return tone === 'positive' || tone === 'negative' || tone === 'warning'
      ? tone
      : 'neutral';
  }

  private asNumber(value: unknown): number {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private asConfidence(value: unknown): 'High' | 'Medium' | 'Low' | undefined {
    const confidence = this.asText(value);
    return confidence === 'High' || confidence === 'Medium' || confidence === 'Low'
      ? confidence
      : undefined;
  }

  private saveNativeTableToHistory(question: string, buyercaddySource?: unknown[], geminiSources?: GeminiSourceItem[]): void {
    if (!buyercaddySource?.length && !geminiSources?.length) {
      return;
    }

    if (this.selectedMode !== ChatCommand.AnalyzeTechStacks && this.selectedMode !== ChatCommand.FindCustomers) {
      return;
    }

    // Собираем колонки как это делается в UI
    const isAggressive = this.aggressiveCustomersMode;
    const baseColumns = [
      'Company',
      'CompanyDomain',
      'Product',
      'Vendor',
      'VendorDomain',
      'Country',
      'HitCount',
      'DateFirstVerified',
      'DateLastVerified',
    ];
    if (isAggressive) {
      baseColumns.push('Source');
    }

    // Нормализация данных
    const getDate = (row: any, upperKey: string, lowerKey: string): string => {
      const v = row?.[upperKey]?.value ?? row?.[lowerKey] ?? row?.[upperKey] ?? '';
      if (!v) return '';
      try {
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.toLocaleDateString();
      } catch {
        return v;
      }
    };

    const getSource = (row: any, fallbackSource: string = '') =>
      row?.Source?.value ?? row?.Source ?? fallbackSource ?? '';

    const normalize = (row: any, fallbackSource: string = '', forceFallback: boolean = false) => ({
      Company: row?.Company ?? row?.company ?? '',
      CompanyDomain: row?.CompanyDomain ?? row?.company_domain ?? row?.companydomain ?? '',
      Product: row?.Product ?? row?.product ?? row?.product_name ?? '',
      Vendor: row?.Vendor ?? row?.vendor ?? row?.product_vendor ?? '',
      VendorDomain: row?.VendorDomain ?? row?.vendordomain ?? row?.product_vendor_domain_name ?? '',
      Country: row?.Country ?? row?.country ?? '',
      HitCount: typeof row?.HitCount === 'number' ? row.HitCount : (row?.HitCount ? Number(row.HitCount) : (row?.hit_count ?? '')),
      DateFirstVerified: getDate(row, 'DateFirstVerified', 'dateFirstVerified'),
      DateLastVerified: getDate(row, 'DateLastVerified', 'dateLastVerified'),
      Source: forceFallback ? fallbackSource : getSource(row, fallbackSource),
    });

    const firstRows = (buyercaddySource ?? []).map(row => normalize(row, 'BuyerCaddy', true));
    const secondRows = (geminiSources ?? []).map(row => normalize(row));
    let rows = [...firstRows, ...secondRows];

    // Формируем Markdown таблицу
    let markdownTable = `**[AUTOMATICALLY SAVED]** Full data extracted from agents for user query.\n\n`;
    markdownTable += `| ${baseColumns.join(' | ')} |\n`;
    markdownTable += `| ${baseColumns.map(() => '---').join(' | ')} |\n`;

    rows.forEach(r => {
      const rowData = baseColumns.map(col => {
        const val = (r as any)[col];
        // Убираем переносы строк и пайпы, чтобы не сломать markdown
        let strVal = val === null || val === undefined ? '' : String(val);
        strVal = strVal.replace(/\n/g, ' ').replace(/\|/g, '\\|');
        return strVal;
      });
      markdownTable += `| ${rowData.join(' | ')} |\n`;
    });

    // Отправляем как отдельную запись истории
    const tableMessage = `[AUTOMATICALLY SAVED] Supplemental data for user request: "${question}"`;
    this.saveHistory(tableMessage, markdownTable);
  }

  private extractCompaniesFromSources(
    buyerCaddyData?: unknown[],
    geminiData?: unknown[],
    mode?: ChatCommand
  ): CompanyDomain[] {
    const combinedData = [...(buyerCaddyData || []), ...(geminiData || [])];
    if (combinedData.length === 0) return [];

    const companyMap = new Map<string, string>(); // domain -> name

    for (const row of combinedData as any[]) {
      let domain = '';
      let name = '';

      if (mode === ChatCommand.AnalyzeTechStacks) {
        // Извлекаем карточки Вендоров продуктов
        domain = row?.VendorDomain?.value ?? row?.VendorDomain ?? row?.vendordomain ?? row?.vendor_domain ?? '';
        name = row?.Vendor?.value ?? row?.Vendor ?? row?.vendor ?? row?.vendor_name ?? '';
      } else {
        // По умолчанию извлекаем карточки самих Компаний
        domain = row?.CompanyDomain?.value ?? row?.CompanyDomain ?? row?.company_domain ?? row?.companydomain ?? '';
        name = row?.Company?.value ?? row?.Company ?? row?.company ?? row?.company_name ?? '';
      }

      if (domain && !companyMap.has(domain.toLowerCase())) {
        companyMap.set(domain.toLowerCase(), name || domain);
      }
    }

    return Array.from(companyMap.entries()).map(([domain, company_name]) => ({
      company_name,
      domain
    }));
  }

  private saveHistory(question: string, response: string): void {
    this.history
      .addHistory({ body: { message: question, response: response } })
      .subscribe();
  }

  private handleError(botMessage: ChatMessage, error: unknown): void {
    console.error('Error:', error);
    this.clearLoadingMessageRotation();
    botMessage.isTyping = false;
    botMessage.text = '<em>Something went wrong. Please try again.</em>';
    this.updateChatHistory();
  }

  clearChatHistory(): void {
    this.chatHistory = [];
    this.updateChatHistory();
    this.geminiAgentService.clearHistory();
    this.clearLoadingMessageRotation();
  }

  generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }
}
